/**
 * The pre-season files (G2.4, G2 test d, ADR 9045, ADR 9007).
 *
 * One builder per page, used twice: by the route's registered export (which reads the whole
 * perimeter, and which the «Colonne» census checks) and by the page's own table, over the rows
 * the planner's filters leave. So the file a planner downloads from the table and the one the
 * census sees are the same file with the same columns, descriptions and provenance.
 *
 * Numbers leave as `raw`: there is no path in `ExportCell` that carries a display string for a
 * number, so "full precision, unrounded" is a type property.
 *
 * **Provenance is stated or declared absent, never invented (ADR 9007).**
 *   - The platform release is read from `GET /version` for every file; a failed read is declared
 *     absent with its reason, never replaced by this interface's own build number.
 *   - A measure is cited only when its column is in the file, defined as the column is computed,
 *     and with `not_registered` as its register version: none of these definitions is a metric
 *     register entry yet, and an empty register version would read as a lookup someone forgot.
 *   - Money is never assigned a currency here. Each amount carries its row's currency, the file
 *     has a currency column, and the column is typed money only when every row shares one.
 */

import { callOperation, collectPages } from '@/api';
import {
  computeOtb, listBuyLines, listClusterSchemes, listPeriodPlans, listSizeCurves, version as platformVersionOperation,
  type BuyLineSummary, type ClusterSchemeSummary, type OtbResponse, type SizeCurveSummary,
} from '@/api/generated';
import { NOT_PUBLISHED, NO_PRINCIPAL, PERIMETER_NOT_RECORDED } from '@/export';
import type { ExportCell, ExportColumn, ExportRequest, MeasureCitation, Provenance, ProvenanceAbsent, Stated } from '@/export';
import { SIZE_EXPORT_COLUMNS, sizeExportCells } from '@/planning';
import { exportDisplay, exportDisplayOf } from '@/planning/words';

import { costProvenanceLabelKey, plansOfNode, UNCLASSIFIED_COST_PROVENANCE_KEY } from './api';
import { curveSizes, keyedSizes } from './headline';
import { planLedgerRows, planNodes, type PlanLedgerRow } from './ledger';
import { curveStoreOf, lineCurveIdOf, rawSharesOf } from './readers';

/** A measure this chapter defines that the metric register does not hold yet. */
const NOT_REGISTERED = 'not_registered';

const PLATFORM_UNREAD: ProvenanceAbsent = { absent: true, reasonKey: 'preseason.export.platformUnread' };
const NOT_REPORTED: ExportCell = { kind: 'missing', state: 'not-reported' };

/** The running platform release as `GET /version` states it — `0.101.0 (abc123def456)` — or its absence. */
async function platformRelease(): Promise<Stated<string>> {
  try {
    const release = await callOperation(platformVersionOperation, {});
    return [release.version, `(${release.commitSha.slice(0, 12)})`].join(' ');
  } catch {
    return PLATFORM_UNREAD;
  }
}

function provenance(measures: readonly Omit<MeasureCitation, 'registerVersion'>[], platformVersion: Stated<string>): Provenance {
  return {
    // The page does not record the perimeter it read; no filter list is claimed (ADR 9007).
    filters: PERIMETER_NOT_RECORDED,
    hierarchyNode: NOT_PUBLISHED,
    timeWindow: NOT_PUBLISHED,
    scenario: NOT_PUBLISHED,
    planVersionId: NOT_PUBLISHED,
    measureDefinitions: measures.map((entry) => ({ ...entry, registerVersion: NOT_REGISTERED })),
    generatedAt: new Date().toISOString(),
    generatedBy: NO_PRINCIPAL,
    platformVersion,
  };
}

const text = (value: string | null | undefined): ExportCell => (
  typeof value === 'string' && value !== '' ? { kind: 'text', value } : NOT_REPORTED
);
const count = (value: number | null | undefined): ExportCell => (
  typeof value === 'number' && Number.isFinite(value) ? { kind: 'integer', raw: value } : NOT_REPORTED
);
const number = (value: number | null | undefined): ExportCell => (
  typeof value === 'number' && Number.isFinite(value) ? { kind: 'number', raw: value } : NOT_REPORTED
);
const flag = (value: boolean | null | undefined): ExportCell => (
  typeof value === 'boolean' ? { kind: 'label', value: String(value), code: String(value) } : NOT_REPORTED
);
const date = (value: string | null | undefined): ExportCell => (
  typeof value === 'string' && value !== '' ? { kind: 'date', iso: value } : NOT_REPORTED
);

/** A money column: typed money in the one currency every row shares, else a number beside the currency column. */
function moneyColumn(key: string, headerKey: string, descriptionKey: string, currencies: readonly string[]): ExportColumn {
  const distinct = [...new Set(currencies)];
  return distinct.length === 1
    ? { key, headerKey, kind: 'money', currency: distinct[0] as string, descriptionKey }
    : { key, headerKey, kind: 'number', descriptionKey };
}

function moneyCell(raw: number, currency: string, single: boolean): ExportCell {
  return single ? { kind: 'money', raw, currency } : { kind: 'number', raw };
}

const flagDisplay = (trueKey: string, falseKey: string) => exportDisplayOf({ true: trueKey, false: falseKey });

// ── the open-to-buy plan ─────────────────────────────────────────────────────

const OTB_MEASURES = [{
  measure: 'preseason.open_to_buy_units',
  definition: 'Planned closing inventory plus planned sales, markdown and shrink, less opening inventory and the on-order book, in units, per plan node and period, as the platform ledger computes it from the stored plan; negative when the period is over-committed.',
}] as const;

/** Every node × period of the plan, its terms and the platform's position, in units. */
export async function planLedgerExportRequest(rows: readonly PlanLedgerRow[]): Promise<ExportRequest> {
  const body: ExportCell[][] = rows.map((row): ExportCell[] => [
    text(row.nodeId),
    text(row.period),
    number(row.openingInventory),
    number(row.plannedSalesUnits),
    number(row.plannedMarkdownUnits),
    number(row.plannedShrinkUnits),
    number(row.closingInventory),
    number(row.onOrderUnits),
    number(row.requirementUnits),
    number(row.coveredUnits),
    number(row.openToBuyUnits),
    flag(row.overCommitted),
  ]);
  const columns: ExportColumn[] = [
    { key: 'node_id', headerKey: 'preseason.column.planNode', kind: 'text', descriptionKey: 'preseason.help.otbScreen.colNode' },
    { key: 'period', headerKey: 'preseason.column.period', kind: 'text', descriptionKey: 'preseason.help.otbScreen.colPeriod' },
    { key: 'opening_inventory', headerKey: 'preseason.column.openingInventory', kind: 'number', unit: 'units', descriptionKey: 'preseason.help.otbScreen.colOpening' },
    { key: 'planned_sales_units', headerKey: 'preseason.column.plannedSales', kind: 'number', unit: 'units', descriptionKey: 'preseason.help.otbScreen.colSales' },
    { key: 'planned_markdown_units', headerKey: 'preseason.column.plannedMarkdown', kind: 'number', unit: 'units', descriptionKey: 'preseason.help.otbScreen.colMarkdown' },
    { key: 'planned_shrink_units', headerKey: 'preseason.column.plannedShrink', kind: 'number', unit: 'units', descriptionKey: 'preseason.help.otbScreen.colShrink' },
    { key: 'closing_inventory', headerKey: 'preseason.column.closingInventory', kind: 'number', unit: 'units', descriptionKey: 'preseason.help.otbScreen.colClosing' },
    { key: 'on_order_units', headerKey: 'preseason.column.onOrder', kind: 'number', unit: 'units', descriptionKey: 'preseason.help.otbScreen.colOnOrder' },
    { key: 'requirement_units', headerKey: 'preseason.column.requirement', kind: 'number', unit: 'units', descriptionKey: 'preseason.help.otbScreen.colRequirement' },
    { key: 'covered_units', headerKey: 'preseason.column.covered', kind: 'number', unit: 'units', descriptionKey: 'preseason.help.otbScreen.colCovered' },
    { key: 'open_to_buy_units', headerKey: 'preseason.column.openToBuy', kind: 'number', unit: 'units', polarity: 'target-relative', descriptionKey: 'preseason.help.otbScreen.colOpenToBuy' },
    {
      key: 'over_committed', headerKey: 'preseason.column.state', kind: 'label',
      display: flagDisplay('preseason.otb.stateOverCommitted', 'preseason.otb.stateOpen'),
      descriptionKey: 'preseason.columnGuide.otb.overCommitted',
    },
  ];
  return {
    id: 'preseason.otb.grid',
    titleKey: 'preseason.otb.title',
    columns,
    rows: () => body,
    rowCount: body.length,
    provenance: provenance(OTB_MEASURES, await platformRelease()),
    perimeter: {
      filters: [],
      sort: [{ key: 'node_id', direction: 'asc' }, { key: 'period', direction: 'asc' }],
      visibleColumns: columns.map((column) => column.key),
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/** The route's file: every stored plan, each node's ledger from `POST /preseason/otb` (computes, writes nothing). */
export async function otbPlanExport(): Promise<ExportRequest> {
  const plans = await collectPages(listPeriodPlans, { query: {} });
  const ledgers = new Map<string, OtbResponse>();
  await Promise.all(planNodes(plans).map(async (nodeId) => {
    try {
      ledgers.set(nodeId, await callOperation(computeOtb, { body: { nodeId, periods: [...plansOfNode(plans, nodeId)] } }));
    } catch {
      // The node's terms still leave; its positions are not reported rather than guessed.
    }
  }));
  return planLedgerExportRequest(planLedgerRows(plans, ledgers));
}

// ── cluster schemes ──────────────────────────────────────────────────────────

const CLUSTER_MEASURES = [{
  measure: 'preseason.cluster_discrimination',
  definition: 'Weighted coefficient of variation of a cluster-level output across the clusters of a scheme, against the threshold the scheme must clear; empty where the scheme has not been scored.',
}] as const;

/** Every scheme version read, the current refresh of each scheme marked, with its discrimination where scored. */
export async function clusterSchemesExportRequest(schemes: readonly ClusterSchemeSummary[]): Promise<ExportRequest> {
  const body: ExportCell[][] = schemes.map((scheme): ExportCell[] => [
    text(scheme.schemeId),
    text(scheme.versionId),
    count(scheme.refreshSequence),
    flag(scheme.isLatestRefresh),
    text(scheme.countryKey),
    { kind: 'label', value: scheme.departmentFamily, code: scheme.departmentFamily },
    { kind: 'label', value: scheme.seasonId, code: scheme.seasonId },
    count(scheme.clusterCount),
    count(scheme.storeCount),
    number(scheme.discrimination?.coefficientOfVariation),
    number(scheme.discrimination?.threshold),
    flag(scheme.discrimination?.discriminates),
    date(scheme.effectiveFrom),
    date(scheme.reviewDate),
    text(scheme.owner),
    flag(scheme.frozen),
  ]);
  const columns: ExportColumn[] = [
    { key: 'scheme_id', headerKey: 'preseason.column.scheme', kind: 'text', descriptionKey: 'preseason.help.clusterScreen.colScheme' },
    { key: 'version_id', headerKey: 'preseason.column.versionId', kind: 'text', descriptionKey: 'preseason.help.clusterScreen.colVersion' },
    { key: 'refresh', headerKey: 'preseason.column.refresh', kind: 'integer', descriptionKey: 'preseason.columnGuide.cluster.refresh' },
    {
      key: 'current_refresh', headerKey: 'preseason.column.currentRefresh', kind: 'label',
      display: flagDisplay('preseason.cluster.currentRefresh', 'preseason.cluster.previousRefresh'),
      descriptionKey: 'preseason.columnGuide.cluster.currentRefresh',
    },
    { key: 'country_key', headerKey: 'preseason.column.market', kind: 'text', descriptionKey: 'preseason.columnGuide.market' },
    { key: 'department_family', headerKey: 'preseason.column.departmentFamily', kind: 'label', descriptionKey: 'preseason.help.clusterScreen.colDepartmentFamily' },
    { key: 'season_id', headerKey: 'preseason.column.season', kind: 'label', descriptionKey: 'preseason.help.clusterScreen.colSeason' },
    { key: 'cluster_count', headerKey: 'preseason.column.clusterCount', kind: 'integer', descriptionKey: 'preseason.help.clusterScreen.colClusterCount' },
    { key: 'store_count', headerKey: 'preseason.column.storeCount', kind: 'integer', descriptionKey: 'preseason.help.clusterScreen.colStoreCount' },
    { key: 'discrimination_cv', headerKey: 'preseason.column.discrimination', kind: 'number', descriptionKey: 'preseason.columnGuide.cluster.discriminationCv' },
    { key: 'discrimination_threshold', headerKey: 'preseason.column.discriminationThreshold', kind: 'number', descriptionKey: 'preseason.columnGuide.cluster.discriminationThreshold' },
    {
      key: 'discriminates', headerKey: 'preseason.column.discriminates', kind: 'label',
      display: flagDisplay('preseason.cluster.discriminates', 'preseason.cluster.doesNotDiscriminate'),
      descriptionKey: 'preseason.columnGuide.cluster.discriminates',
    },
    { key: 'effective_from', headerKey: 'preseason.column.effectiveFrom', kind: 'date', descriptionKey: 'preseason.columnGuide.cluster.effectiveFrom' },
    { key: 'review_date', headerKey: 'preseason.column.reviewDate', kind: 'date', descriptionKey: 'preseason.help.clusterScreen.colReviewDate' },
    { key: 'owner', headerKey: 'preseason.column.owner', kind: 'text', descriptionKey: 'preseason.help.clusterScreen.colOwner' },
    {
      key: 'frozen', headerKey: 'preseason.column.state', kind: 'label',
      display: flagDisplay('preseason.cluster.frozen', 'preseason.cluster.open'),
      descriptionKey: 'preseason.help.clusterScreen.colFrozen',
    },
  ];
  return {
    id: 'preseason.cluster.grid',
    titleKey: 'preseason.cluster.title',
    columns,
    rows: () => body,
    rowCount: body.length,
    provenance: provenance(CLUSTER_MEASURES, await platformRelease()),
    perimeter: {
      filters: [],
      sort: [{ key: 'scheme_id', direction: 'asc' }, { key: 'refresh', direction: 'asc' }],
      visibleColumns: columns.map((column) => column.key),
      drillPath: ['scheme'],
      densityMode: 'compact',
    },
  };
}

export async function clusterExport(): Promise<ExportRequest> {
  return clusterSchemesExportRequest(await collectPages(listClusterSchemes, { query: {} }));
}

// ── size curves ──────────────────────────────────────────────────────────────

const RESOLVED_LEVEL_KEY: Readonly<Record<string, string>> = {
  store: 'preseason.resolvedLevel.store',
  cluster: 'preseason.resolvedLevel.cluster',
  category: 'preseason.resolvedLevel.category',
};

/** A level code in words, or `null` for a code this build does not know (shown as written). */
export function resolvedLevelKey(level: string): string | null {
  return RESOLVED_LEVEL_KEY[level] ?? null;
}

const SIZE_CURVE_MEASURES = [
  {
    measure: 'preseason.corrected_size_share',
    definition: 'Size share estimated by expectation-maximisation over censored weekly observations; a door curve is shrunk toward its article curve with an empirical-Bayes weight.',
  },
  {
    measure: 'preseason.raw_size_share',
    definition: 'Size share of observed units with no correction for weeks in which a size was unavailable; published for article curves only.',
  },
] as const;

/** Every curve × size: the curve's identity and scope, the size by name, raw and corrected share. */
export async function sizeCurveExportRequest(curves: readonly SizeCurveSummary[]): Promise<ExportRequest> {
  const body: ExportCell[][] = curves.flatMap((curve) => {
    const raw = rawSharesOf(curve);
    return curveSizes(curve).map((name): ExportCell[] => [
      text(curve.curveId),
      text(curve.versionId),
      text(curve.categoryKey),
      // An article curve fitted over every door carries no cluster: its level says so.
      curve.resolvedLevel === 'category' ? NOT_REPORTED : text(curve.clusterId),
      text(curveStoreOf(curve)),
      { kind: 'label', value: curve.resolvedLevel, code: curve.resolvedLevel },
      text(curve.countryKey),
      text(curve.channelKey),
      text(curve.seasonId),
      text(curve.sizeScaleId),
      ...sizeExportCells(name),
      number(raw?.[name.key]),
      number(curve.shares[name.key]),
      number(curve.censoringRate),
      number(curve.shrinkageWeight),
      text(curve.modelVersionId),
      text(curve.batchId),
    ]);
  });
  const columns: ExportColumn[] = [
    { key: 'curve_id', headerKey: 'preseason.column.curve', kind: 'text', descriptionKey: 'preseason.help.sizeCurveScreen.colCurve' },
    { key: 'curve_version_id', headerKey: 'preseason.column.curveVersion', kind: 'text', descriptionKey: 'preseason.help.sizeCurveScreen.colVersion' },
    { key: 'article_key', headerKey: 'preseason.column.article', kind: 'text', descriptionKey: 'preseason.help.sizeCurveScreen.colCategory' },
    { key: 'cluster_id', headerKey: 'preseason.column.cluster', kind: 'text', descriptionKey: 'preseason.columnGuide.sizeCurve.cluster' },
    { key: 'store_key', headerKey: 'preseason.column.door', kind: 'text', descriptionKey: 'preseason.columnGuide.sizeCurve.store' },
    {
      key: 'resolved_level', headerKey: 'preseason.column.resolvedLevel', kind: 'label',
      display: exportDisplay(resolvedLevelKey), descriptionKey: 'preseason.help.sizeCurveScreen.colResolvedLevel',
    },
    { key: 'country_key', headerKey: 'preseason.column.market', kind: 'text', descriptionKey: 'preseason.columnGuide.market' },
    { key: 'channel_key', headerKey: 'preseason.column.channel', kind: 'text', descriptionKey: 'preseason.columnGuide.sizeCurve.channel' },
    { key: 'season_id', headerKey: 'preseason.column.season', kind: 'text', descriptionKey: 'preseason.columnGuide.sizeCurve.season' },
    { key: 'size_scale_id', headerKey: 'preseason.column.sizeScale', kind: 'text', descriptionKey: 'preseason.columnGuide.sizeCurve.sizeScale' },
    ...SIZE_EXPORT_COLUMNS,
    { key: 'raw_share', headerKey: 'preseason.column.rawShare', kind: 'number', descriptionKey: 'preseason.columnGuide.sizeCurve.rawShare' },
    { key: 'corrected_share', headerKey: 'preseason.column.correctedShare', kind: 'number', descriptionKey: 'preseason.columnGuide.sizeCurve.correctedShare' },
    { key: 'censoring_rate', headerKey: 'preseason.column.censoringRate', kind: 'number', descriptionKey: 'preseason.help.sizeCurveScreen.colCensoring' },
    { key: 'shrinkage_weight', headerKey: 'preseason.sizeCurve.shrinkageWeight', kind: 'number', descriptionKey: 'preseason.help.sizeCurveScreen.colShrinkage' },
    { key: 'model_version_id', headerKey: 'preseason.column.modelVersion', kind: 'text', descriptionKey: 'preseason.columnGuide.sizeCurve.modelVersion' },
    { key: 'batch_id', headerKey: 'preseason.column.batch', kind: 'text', descriptionKey: 'preseason.columnGuide.sizeCurve.batch' },
  ];
  return {
    id: 'preseason.sizeCurve.chart',
    titleKey: 'preseason.sizeCurve.title',
    columns,
    rows: () => body,
    rowCount: body.length,
    provenance: provenance(SIZE_CURVE_MEASURES, await platformRelease()),
    perimeter: {
      filters: [],
      sort: [{ key: 'curve_id', direction: 'asc' }, { key: 'size_run_position', direction: 'asc' }],
      visibleColumns: columns.map((column) => column.key),
      drillPath: ['article', 'cluster'],
      densityMode: 'compact',
    },
  };
}

export async function sizeCurveExport(): Promise<ExportRequest> {
  return sizeCurveExportRequest(await collectPages(listSizeCurves, { query: {} }));
}

// ── buy lines, by size ───────────────────────────────────────────────────────

const BUY_MEASURES = [{
  measure: 'preseason.buy_quantity_units',
  definition: 'Units a buy line plans to order, as published on the line, split by size; the planned quantity, not the depth the buy diagnosis derives.',
}] as const;

/**
 * Every buy line × size: the line's chain references (plan version, option, cluster, supplier,
 * receipt week, the curve it cites), its cost with the currency it is in, and the units of each
 * size in run order. A line's units by size add up to its units, so the file both lists the lines
 * and carries the split a buyer sends to the supplier. A line with no size split leaves one row,
 * its size cells not reported.
 */
export async function buyLinesExportRequest(lines: readonly BuyLineSummary[]): Promise<ExportRequest> {
  const currencies = lines.map((line) => line.currencyCode);
  const single = new Set(currencies).size === 1;
  const body: ExportCell[][] = lines.flatMap((line) => {
    const head: ExportCell[] = [
      text(line.buyLineId),
      text(line.planVersionId),
      text(line.sourceOptionId),
      text(line.clusterId),
      text(line.supplierKey),
      text(line.receiptPeriodKey),
      count(line.quantityUnits),
      text(line.currencyCode),
      moneyCell(line.unitCostAmount, line.currencyCode, single),
      text(lineCurveIdOf(line)),
      text(line.sizeCurveVersionId),
      { kind: 'label', value: line.costProvenance, code: line.costProvenance },
    ];
    const names = keyedSizes(Object.keys(line.sizeQuantities), line.sizes);
    if (names.length === 0) return [[...head, ...SIZE_EXPORT_COLUMNS.map(() => NOT_REPORTED), NOT_REPORTED]];
    return names.map((name) => [...head, ...sizeExportCells(name), count(line.sizeQuantities[name.key])]);
  });
  const columns: ExportColumn[] = [
    { key: 'buy_line_id', headerKey: 'preseason.column.buyLine', kind: 'text', descriptionKey: 'preseason.columnGuide.buy.buyLineId' },
    { key: 'plan_version_id', headerKey: 'preseason.column.planVersion', kind: 'text', descriptionKey: 'preseason.columnGuide.buy.planVersion' },
    { key: 'source_option_id', headerKey: 'preseason.column.option', kind: 'text', descriptionKey: 'preseason.help.buyScreen.colOption' },
    { key: 'cluster_id', headerKey: 'preseason.column.cluster', kind: 'text', descriptionKey: 'preseason.help.buyScreen.colCluster' },
    { key: 'supplier_key', headerKey: 'preseason.column.supplier', kind: 'text', descriptionKey: 'preseason.help.buyScreen.colSupplier' },
    { key: 'receipt_period_key', headerKey: 'preseason.column.receiptWeek', kind: 'text', descriptionKey: 'preseason.columnGuide.buy.receiptPeriod' },
    { key: 'quantity_units', headerKey: 'preseason.column.units', kind: 'integer', unit: 'units', descriptionKey: 'preseason.columnGuide.buy.units' },
    { key: 'currency_code', headerKey: 'preseason.column.currency', kind: 'text', descriptionKey: 'preseason.columnGuide.buy.currency' },
    moneyColumn('unit_cost_amount', 'preseason.column.unitCost', 'preseason.columnGuide.buy.unitCost', currencies),
    { key: 'size_curve_id', headerKey: 'preseason.column.curve', kind: 'text', descriptionKey: 'preseason.columnGuide.buy.curve' },
    { key: 'size_curve_version_id', headerKey: 'preseason.column.curveVersion', kind: 'text', descriptionKey: 'preseason.columnGuide.buy.curveVersion' },
    {
      key: 'cost_provenance', headerKey: 'preseason.column.costProvenance', kind: 'label',
      display: exportDisplay((code) => (costProvenanceLabelKey(code) === UNCLASSIFIED_COST_PROVENANCE_KEY ? null : costProvenanceLabelKey(code))),
      descriptionKey: 'preseason.columnGuide.buy.costProvenance',
    },
    ...SIZE_EXPORT_COLUMNS,
    { key: 'size_units', headerKey: 'preseason.column.sizeUnits', kind: 'integer', unit: 'units', descriptionKey: 'preseason.columnGuide.buy.sizeUnits' },
  ];
  return {
    id: 'preseason.buy.grid',
    titleKey: 'preseason.buy.title',
    columns,
    rows: () => body,
    rowCount: body.length,
    provenance: provenance(BUY_MEASURES, await platformRelease()),
    perimeter: {
      filters: [],
      sort: [
        { key: 'receipt_period_key', direction: 'asc' },
        { key: 'buy_line_id', direction: 'asc' },
        { key: 'size_run_position', direction: 'asc' },
      ],
      visibleColumns: columns.map((column) => column.key),
      drillPath: ['buy'],
      densityMode: 'compact',
    },
  };
}

export async function buyExport(): Promise<ExportRequest> {
  return buyLinesExportRequest(await collectPages(listBuyLines, { query: {} }));
}
